import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { MeteoConditionIcon } from './MeteoConditionIcon';
import {
  formatTempC,
  type MeteoHourly,
  type WeatherPayload,
} from '../../services/meteoApi';
import { useWeather } from '../../hooks/useWeather';
import {
  ensureServerTimeSync,
  getServerAdjustedNow,
  getServerTimeZone,
  subscribeServerTime,
} from '../../utils/serverTime';

interface WeatherDialogProps {
  open: boolean;
  onClose: () => void;
  data: WeatherPayload | null;
}

type ForecastView = 'hourly' | 'days16';

const expandBtnSx = {
  textTransform: 'none' as const,
  color: '#2391FF',
  border: '1px solid #2391FF',
  mb: 1,
  py: 0.5,
};

function localHourKey(date: Date, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}`;
  } catch {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}`;
  }
}

function slotHourKey(isoTs: string): string {
  return String(isoTs).replace(' ', 'T').slice(0, 13);
}

function dayKeyFromTs(isoTs: string): string {
  return String(isoTs).replace(' ', 'T').slice(0, 10);
}

const WeatherDialog: React.FC<WeatherDialogProps> = ({ open, onClose, data }) => {
  const { t, i18n } = useTranslation();
  const { refresh, loading } = useWeather({ enabled: open });
  const [view, setView] = useState<ForecastView>('hourly');
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const locale = i18n.language || 'es';
  const hourlyListRef = useRef<HTMLDivElement | null>(null);
  const currentHourRowRef = useRef<HTMLDivElement | null>(null);

  const handleRefresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    try {
      await refresh(true);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setView('hourly');
      setExpandedDay(null);
    }
  }, [open]);

  const changeView = (next: ForecastView) => {
    setExpandedDay(null);
    setView(next);
  };

  const days = useMemo(() => {
    if (view !== 'days16') return [];
    return (data?.daily || []).slice(0, 16);
  }, [data, view]);

  const allHourly = useMemo(() => data?.hourly || [], [data]);

  const meteoTz =
    data?.location?.timezone || getServerTimeZone() || 'America/Argentina/Buenos_Aires';

  const [serverTimeRev, setServerTimeRev] = useState(0);
  useEffect(() => subscribeServerTime((ev) => {
    if (ev.offsetCorrected) setServerTimeRev((n) => n + 1);
  }), []);

  const nowHourKey = useMemo(() => {
    ensureServerTimeSync();
    void open;
    void data?.updated_at;
    void serverTimeRev;
    return localHourKey(getServerAdjustedNow(), meteoTz);
  }, [meteoTz, open, data?.updated_at, serverTimeRev]);

  const todayKey = nowHourKey.slice(0, 10);

  // Vista inicial: solo horas de hoy
  const hoursToday = useMemo(
    () => allHourly.filter((h) => dayKeyFromTs(h.time) === todayKey),
    [allHourly, todayKey]
  );

  const isPastHour = (isoTs: string) => slotHourKey(isoTs) < nowHourKey;

  const currentHourIndex = useMemo(() => {
    const exact = hoursToday.findIndex((h) => slotHourKey(h.time) === nowHourKey);
    if (exact >= 0) return exact;
    const next = hoursToday.findIndex((h) => slotHourKey(h.time) >= nowHourKey);
    return next >= 0 ? next : Math.max(0, hoursToday.length - 1);
  }, [hoursToday, nowHourKey]);

  useEffect(() => {
    if (!open || view !== 'hourly' || hoursToday.length === 0) return undefined;
    let cancelled = false;
    const scrollToNow = () => {
      if (cancelled) return;
      const list = hourlyListRef.current;
      const row = currentHourRowRef.current;
      if (!list || !row) return;
      const listRect = list.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      list.scrollTop += rowRect.top - listRect.top;
    };
    const t1 = window.setTimeout(scrollToNow, 0);
    const t2 = window.setTimeout(scrollToNow, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [open, view, hoursToday, currentHourIndex]);

  const hoursForDay = (date: string) =>
    allHourly.filter((h) => dayKeyFromTs(h.time) === date);

  const toggleDay = (date: string) => {
    setExpandedDay((prev) => (prev === date ? null : date));
  };

  const current = data?.current;
  const locationLabel = data?.location?.display || data?.location?.name || '';

  const conditionLabel = (labelKey?: string) =>
    t(`meteo.conditions.${labelKey || 'unknown'}`, {
      defaultValue: t('meteo.conditions.unknown'),
    });

  const formatDay = (isoDate: string) => {
    try {
      const d = new Date(`${isoDate}T12:00:00`);
      return d.toLocaleDateString(locale.startsWith('es') ? 'es-AR' : 'en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    } catch {
      return isoDate;
    }
  };

  const formatHour = (isoTs: string) => {
    try {
      const part = isoTs.includes('T') ? isoTs.split('T')[1] : isoTs;
      return (part || '').slice(0, 5);
    } catch {
      return isoTs;
    }
  };

  /** Fecha/hora compacta de última actualización (p.ej. 2/9 18:00). */
  const updatedAtLabel = useMemo(() => {
    const iso = data?.updated_at;
    if (!iso) return null;
    try {
      const normalized = String(iso).replace(' ', 'T');
      const [datePart, timePart = ''] = normalized.split('T');
      const bits = datePart.split('-');
      if (bits.length < 3) return null;
      const month = Number(bits[1]);
      const day = Number(bits[2]);
      const hm = timePart.slice(0, 5);
      if (!month || !day || hm.length < 4) return null;
      return locale.startsWith('es') ? `${day}/${month} ${hm}` : `${month}/${day} ${hm}`;
    } catch {
      return null;
    }
  }, [data?.updated_at, locale]);

  const sectionTitle =
    view === 'hourly' ? t('meteo.dialog.forecastHourly') : t('meteo.dialog.forecast16');

  const renderHourRows = (
    list: MeteoHourly[],
    opts?: { scrollable?: boolean; highlightCurrent?: boolean }
  ) => {
    if (list.length === 0) {
      return (
        <Typography sx={{ fontSize: '0.8rem', color: '#888', py: 1 }}>
          {t('meteo.dialog.noHourly')}
        </Typography>
      );
    }
    return (
      <Box
        ref={opts?.scrollable ? hourlyListRef : undefined}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.35,
          ...(opts?.scrollable
            ? { mb: 1.25, maxHeight: 280, overflowY: 'auto' }
            : { maxHeight: 220, overflowY: 'auto', mt: 0.5, mb: 0.5, pl: 0.5 }),
        }}
      >
        {list.map((h, idx) => {
          const past = opts?.highlightCurrent ? isPastHour(h.time) : false;
          const isCurrent = Boolean(opts?.highlightCurrent && idx === currentHourIndex);
          return (
            <Box
              key={h.time}
              ref={isCurrent ? currentHourRowRef : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.35,
                borderBottom: '1px solid #2a2a2a',
                opacity: past ? 0.45 : 1,
                backgroundColor: isCurrent ? 'rgba(35,145,255,0.12)' : 'transparent',
                borderRadius: isCurrent ? 0.5 : 0,
              }}
            >
              <Typography
                sx={{
                  width: 44,
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  color: isCurrent ? '#fff' : '#ccc',
                  fontWeight: isCurrent ? 700 : 400,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatHour(h.time)}
              </Typography>
              <MeteoConditionIcon
                icon={h.icon}
                isDay={h.is_day !== false}
                sx={{ fontSize: 18 }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: '0.72rem',
                  color: '#aaa',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {conditionLabel(h.label_key)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontVariantNumeric: 'tabular-nums',
                  color: '#fff',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatTempC(h.temperature_c, locale)}
              </Typography>
              <Typography
                sx={{
                  width: 36,
                  textAlign: 'right',
                  fontSize: '0.7rem',
                  color: '#6eb5ff',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {h.precip_prob != null ? `${Math.round(h.precip_prob)}%` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          color: '#fff',
          border: '1px solid #333',
          borderRadius: 2,
          // Más arriba para no pegar al nav inferior en móvil.
          transform: 'translateY(-20px)',
          // Vista extendida: recuadro ~5px más bajo (MUI default = 100% - 64px).
          ...(view === 'days16' ? { maxHeight: 'calc(100% - 69px)' } : null),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          py: 1.25,
          borderBottom: '1px solid #333',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('meteo.dialog.title')}
          </Typography>
          {locationLabel ? (
            <Typography sx={{ fontSize: '0.75rem', color: '#aaa', mt: 0.25 }} noWrap>
              {locationLabel}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
            ml: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('meteo.dialog.refresh')}>
              <span>
                <IconButton
                  onClick={() => void handleRefresh()}
                  size="small"
                  disabled={refreshing}
                  sx={{ color: '#888', '&:hover': { color: '#ccc' } }}
                  aria-label={t('meteo.dialog.refresh')}
                >
                  {refreshing ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: '#888' }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small" sx={{ color: '#aaa' }} aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {updatedAtLabel ? (
            <Typography
              sx={{
                fontSize: '0.58rem',
                color: '#666',
                lineHeight: 1,
                mt: -0.35,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: 0.1,
                userSelect: 'none',
              }}
              title={data?.updated_at || undefined}
            >
              {updatedAtLabel}
            </Typography>
          ) : null}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 2,
          pt: 1.5,
          // En extendido achicar el hueco bajo el último día.
          pb: view === 'days16' ? 0.5 : 1.5,
        }}
      >
        {!data?.configured ? (
          <Typography sx={{ color: '#ccc', fontSize: '0.9rem', py: 2 }}>
            {t('meteo.dialog.notConfigured')}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1.5,
                mt: 0.5,
              }}
            >
              <MeteoConditionIcon
                icon={current?.icon}
                isDay={current?.is_day !== false}
                sx={{ fontSize: 40 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    lineHeight: 1.1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatTempC(current?.temperature_c, locale)}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#ccc' }}>
                  {conditionLabel(current?.label_key)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.75,
                mb: 1.75,
                fontSize: '0.78rem',
                color: '#bbb',
              }}
            >
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {t('meteo.dialog.feelsLike')}: {formatTempC(current?.feels_like_c, locale)}
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {t('meteo.dialog.humidity')}:{' '}
                {current?.humidity != null ? `${Math.round(current.humidity)}%` : '—'}
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {t('meteo.dialog.wind')}:{' '}
                {current?.wind_kmh != null
                  ? `${Math.round(current.wind_kmh)} km/h`
                  : '—'}
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {t('meteo.dialog.precip')}:{' '}
                {current?.precip_mm != null ? `${current.precip_mm} mm` : '—'}
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {t('meteo.dialog.clouds')}:{' '}
                {current?.cloud_cover != null ? `${Math.round(current.cloud_cover)}%` : '—'}
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {t('meteo.dialog.pressure')}:{' '}
                {current?.pressure_hpa != null
                  ? `${Math.round(current.pressure_hpa)} hPa`
                  : '—'}
              </Typography>
            </Box>

            {data.stale ? (
              <Typography sx={{ fontSize: '0.7rem', color: '#f0ad4e', mb: 1 }}>
                {t('meteo.dialog.stale')}
              </Typography>
            ) : null}

            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#2391FF',
                mb: 0.75,
              }}
            >
              {sectionTitle}
            </Typography>

            {view === 'hourly' ? (
              renderHourRows(hoursToday, { scrollable: true, highlightCurrent: true })
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  mb: 0,
                }}
              >
                {view === 'days16' && (
                  <Typography sx={{ fontSize: '0.68rem', color: '#777', mb: 0.5 }}>
                    {t('meteo.dialog.tapDayHourly')}
                  </Typography>
                )}
                {days.map((d) => {
                  const openDay = expandedDay === d.date;
                  const dayHours = openDay ? hoursForDay(d.date) : [];
                  return (
                    <Box key={d.date} sx={{ borderBottom: '1px solid #2a2a2a' }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => toggleDay(d.date)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.55,
                          width: '100%',
                          m: 0,
                          px: 0.25,
                          border: 'none',
                          background: openDay ? 'rgba(35,145,255,0.1)' : 'transparent',
                          cursor: 'pointer',
                          color: 'inherit',
                          textAlign: 'left',
                          borderRadius: 0.5,
                          '&:hover': { backgroundColor: 'rgba(35,145,255,0.08)' },
                        }}
                      >
                        <Typography
                          sx={{
                            width: 92,
                            flexShrink: 0,
                            fontSize: '0.75rem',
                            color: '#ccc',
                            textTransform: 'capitalize',
                          }}
                        >
                          {formatDay(d.date)}
                        </Typography>
                        <MeteoConditionIcon icon={d.icon} sx={{ fontSize: 18 }} />
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: '0.72rem',
                            color: '#aaa',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {conditionLabel(d.label_key)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontVariantNumeric: 'tabular-nums',
                            color: '#fff',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatTempC(d.tmin_c, locale).replace('°C', '')}
                          {' / '}
                          {formatTempC(d.tmax_c, locale)}
                        </Typography>
                        <Typography
                          sx={{
                            width: 32,
                            textAlign: 'right',
                            fontSize: '0.7rem',
                            color: '#6eb5ff',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {d.precip_prob != null ? `${Math.round(d.precip_prob)}%` : ''}
                        </Typography>
                        {openDay ? (
                          <ExpandLess sx={{ fontSize: 18, color: '#2391FF' }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                        )}
                      </Box>
                      {openDay ? renderHourRows(dayHours) : null}
                    </Box>
                  );
                })}
              </Box>
            )}

            {view === 'hourly' ? (
              <Button fullWidth size="small" onClick={() => changeView('days16')} sx={expandBtnSx}>
                {t('meteo.dialog.extend')}
              </Button>
            ) : null}

            {/* Atribución Open-Meteo: oculta por el momento (el backend sigue enviando attribution*).
               Para reactivar: quitar el `false &&` y dejar `data?.attribution_url ? ( ... ) : null`. */}
            {false && data?.attribution_url ? (
              <Typography sx={{ fontSize: '0.65rem', color: '#777', textAlign: 'center' }}>
                <Link
                  href={data?.attribution_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#888', textDecoration: 'underline' }}
                >
                  {data?.attribution || 'Open-Meteo'}
                </Link>
              </Typography>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WeatherDialog;
